import { useMemo, useState, type MutableRefObject } from 'react';
import { Crown, ChevronDown } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { toDateStr } from '../../utils/dates';
import { computeFlightStats } from '../../utils/flightStats';
import type { Temporal } from '../../utils/flightStats';
import { computeAchievements, type Achievement } from '../../utils/achievements';
import { getCityName } from '../../utils/airports';
import { getAircraftDisplayName } from '../../utils/aircraftTypes';
import { RouteMap } from './RouteMap';
import { AchievementBadge } from './AchievementBadge';
import { AchievementCelebration } from './AchievementCelebration';
import { AchievementDetail } from './AchievementDetail';
import { PremiumBadge } from '../premium/PremiumBadge';
import { PremiumUpsellSheet } from '../premium/PremiumUpsellSheet';
import { isPremiumActive } from '../../utils/premiumGating';
import type { Flight, User } from '../../types';

/** Flip to true to show past/future route toggles on the globe */
const SHOW_TEMPORAL_TOGGLE = false;

interface FlightStatsProps {
  flights?: Flight[];
  user?: User;
  friendCount?: number;
  referralCount?: number;
  globeSnapshotRef?: MutableRefObject<(() => Promise<Blob>) | null>;
}

const COLLAPSED_COUNT = 5;

function temporalVisible(temporal: Temporal | undefined, showPast: boolean, showFuture: boolean): boolean {
  if (!temporal) return showPast; // default to past for backward compat
  if (temporal === 'past') return showPast;
  if (temporal === 'future') return showFuture;
  // 'both' — visible if either toggle is on
  return showPast || showFuture;
}

export function FlightStats({ flights: propFlights, user: propUser, friendCount: propFriendCount, referralCount: propReferralCount, globeSnapshotRef }: FlightStatsProps = {}) {
  const { flights: ctxFlights, friends, currentUser, isPremium, referralCount: ctxReferralCount } = useApp();
  const [showUpsell, setShowUpsell] = useState(false);
  const [airportsExpanded, setAirportsExpanded] = useState(false);
  const [aircraftExpanded, setAircraftExpanded] = useState(false);
  const [showPast, setShowPast] = useState(true);
  const [showFuture, setShowFuture] = useState(true);
  const [selectedAchievement, setSelectedAchievement] = useState<Achievement | null>(null);

  const allFlights = propFlights ?? ctxFlights;
  const user = propUser ?? currentUser;
  const numFriends = propFriendCount ?? friends.length;
  const isOwnProfile = !propUser || propUser.id === currentUser.id;
  // Only use current user's referral count for own profile; friends default to 0
  const numReferrals = propReferralCount ?? (isOwnProfile ? ctxReferralCount : 0);
  const profileIsPremium = isOwnProfile ? isPremium : isPremiumActive(user);
  const gated = !profileIsPremium;

  const today = toDateStr(new Date());

  // Compute stats from ALL flights — stats numbers will only count past flights internally
  const stats = useMemo(() => computeFlightStats(allFlights, user.baseAirport, today), [allFlights, user.baseAirport, today]);

  // Past flights for achievements
  const pastFlights = useMemo(() => allFlights.filter((f) => f.date <= today), [allFlights, today]);
  const hasFutureFlights = useMemo(() => allFlights.some((f) => f.date > today), [allFlights, today]);

  const achievements = useMemo(
    () => computeAchievements(stats, numFriends, pastFlights, numReferrals),
    [stats, numFriends, pastFlights, numReferrals],
  );

  // Filter routes and airports for the globe based on toggle state
  const visibleRoutes = useMemo(
    () => stats.temporalRoutes.filter((r) => temporalVisible(r.temporal, showPast, showFuture)),
    [stats.temporalRoutes, showPast, showFuture],
  );
  const visibleGlobeAirports = useMemo(
    () => stats.temporalAirports.filter((a) => temporalVisible(a.temporal, showPast, showFuture)),
    [stats.temporalAirports, showPast, showFuture],
  );

  if (allFlights.length === 0) {
    return (
      <div className="mx-3 mt-3 bg-slate-800 rounded-xl p-6 border border-slate-700/50 text-center">
        <span className="text-3xl block mb-2">✈️</span>
        <p className="text-sm text-slate-400">Add your first flights to see your flight map</p>
      </div>
    );
  }

  const maxAirportCount = stats.topAirports[0]?.count ?? 1;
  const maxAircraftCount = stats.topAircraft[0]?.count ?? 1;
  const visibleAirports = airportsExpanded ? stats.topAirports : stats.topAirports.slice(0, COLLAPSED_COUNT);
  const visibleAircraft = aircraftExpanded ? stats.topAircraft : stats.topAircraft.slice(0, COLLAPSED_COUNT);
  const hasMoreAirports = stats.topAirports.length > COLLAPSED_COUNT;
  const hasMoreAircraft = stats.topAircraft.length > COLLAPSED_COUNT;

  return (
    <div className="space-y-3 mt-3">
      {/* Stat row */}
      <div className="flex justify-around px-3 py-1">
        <StatPill value={stats.totalFlights} label="Flights" />
        <StatPill value={formatNumber(stats.totalNauticalMiles)} label="Nautical mi" divider />
        <StatPill value={`${stats.totalEstimatedHours}h`} label="Hours" divider />
        <StatPill value={stats.uniqueAirports.length} label="Airports" divider />
      </div>

      {/* Past/Future toggle pills — hidden for now, set SHOW_TEMPORAL_TOGGLE=true to re-enable */}
      {SHOW_TEMPORAL_TOGGLE && hasFutureFlights && !gated && (
        <div className="flex items-center justify-center gap-2 px-3">
          <button
            onClick={() => setShowPast((v) => !v)}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              showPast
                ? 'bg-[rgba(220,80,70,0.15)] text-[rgb(220,80,70)]'
                : 'bg-slate-800 text-slate-500'
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${showPast ? 'bg-[rgb(220,80,70)]' : 'bg-slate-600'}`} />
            Past
          </button>
          <button
            onClick={() => setShowFuture((v) => !v)}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              showFuture
                ? 'bg-[rgba(100,180,220,0.15)] text-[rgb(100,180,220)]'
                : 'bg-slate-800 text-slate-500'
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${showFuture ? 'bg-[rgb(100,180,220)]' : 'bg-slate-600'}`} />
            Future
          </button>
        </div>
      )}

      {/* 3D Globe */}
      {gated ? (
        <button
          onClick={() => setShowUpsell(true)}
          className="mx-3 bg-slate-900 rounded-xl overflow-hidden border border-slate-700/50 relative active:scale-[0.99] transition-transform"
        >
          <div className="opacity-20 blur-[2px] pointer-events-none">
            <RouteMap
              routes={stats.uniqueRoutes}
              airports={stats.uniqueAirports}
              baseAirport={user.baseAirport}
            />
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <Crown size={28} className="text-amber-400 mb-1.5" />
            <span className="text-sm font-semibold text-amber-400">Premium Feature</span>
            <span className="text-xs text-slate-500 mt-0.5">Tap to learn more</span>
          </div>
        </button>
      ) : (
        <div className="mx-3 bg-slate-900 rounded-xl overflow-hidden border border-slate-700/50">
          <RouteMap
            routes={visibleRoutes}
            airports={visibleGlobeAirports}
            baseAirport={user.baseAirport}
            snapshotRef={globeSnapshotRef}
          />
        </div>
      )}

      {/* Achievements */}
      <div className="mx-3 bg-slate-800 rounded-xl p-3 border border-slate-700/50 relative">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
            Achievements
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-600">
              {achievements.filter(a => a.unlocked).length}/{achievements.length}
            </span>
            {gated && <PremiumBadge onClick={() => setShowUpsell(true)} />}
          </div>
        </div>
        <div className={`grid grid-cols-4 gap-2 ${gated ? 'opacity-30 blur-[2px] pointer-events-none' : ''}`}>
          {achievements.map((a) => (
            <AchievementBadge key={a.id} achievement={a} onClick={() => setSelectedAchievement(a)} />
          ))}
        </div>
      </div>

      {/* Top Airports */}
      {stats.topAirports.length > 0 && (
        <div className="mx-3 bg-slate-800 rounded-xl p-3 border border-slate-700/50 relative">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Top Airports
            </h3>
            {gated && <PremiumBadge onClick={() => setShowUpsell(true)} />}
          </div>
          <div className={`space-y-1.5 ${gated ? 'opacity-30 blur-[2px] pointer-events-none' : ''}`}>
            {visibleAirports.map((ap) => (
              <div key={ap.iata} className="flex items-center gap-2">
                <span className="text-xs font-mono text-slate-300 w-8">{ap.iata}</span>
                <div className="flex-1 h-5 bg-slate-700/50 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-accent/60 flex items-center pl-2"
                    style={{ width: `${Math.max(20, (ap.count / maxAirportCount) * 100)}%` }}
                  >
                    <span className="text-[10px] font-semibold text-white/80">{ap.count}</span>
                  </div>
                </div>
                <span className="text-[10px] text-slate-500 w-24 text-right truncate">
                  {getCityName(ap.iata)}
                </span>
              </div>
            ))}
            {hasMoreAirports && (
              <button
                onClick={() => setAirportsExpanded(!airportsExpanded)}
                className="flex items-center gap-1 mx-auto pt-1 text-[11px] text-accent font-medium active:opacity-70"
              >
                {airportsExpanded ? 'Show less' : `+${stats.topAirports.length - COLLAPSED_COUNT} more`}
                <ChevronDown size={12} className={`transition-transform ${airportsExpanded ? 'rotate-180' : ''}`} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Top Aircraft */}
      {stats.topAircraft.length > 0 && (
        <div className="mx-3 bg-slate-800 rounded-xl p-3 border border-slate-700/50 relative">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Top Aircraft
            </h3>
            {gated && <PremiumBadge onClick={() => setShowUpsell(true)} />}
          </div>
          <div className={`space-y-1.5 ${gated ? 'opacity-30 blur-[2px] pointer-events-none' : ''}`}>
            {visibleAircraft.map((ac) => (
              <div key={ac.type} className="flex items-center gap-2">
                <span className="text-xs font-mono text-slate-300 w-20 truncate" title={ac.type}>{getAircraftDisplayName(ac.type)}</span>
                <div className="flex-1 h-5 bg-slate-700/50 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-warm-teal/60 flex items-center pl-2"
                    style={{ width: `${Math.max(20, (ac.count / maxAircraftCount) * 100)}%` }}
                  >
                    <span className="text-[10px] font-semibold text-white/80">{ac.count}</span>
                  </div>
                </div>
              </div>
            ))}
            {hasMoreAircraft && (
              <button
                onClick={() => setAircraftExpanded(!aircraftExpanded)}
                className="flex items-center gap-1 mx-auto pt-1 text-[11px] text-accent font-medium active:opacity-70"
              >
                {aircraftExpanded ? 'Show less' : `+${stats.topAircraft.length - COLLAPSED_COUNT} more`}
                <ChevronDown size={12} className={`transition-transform ${aircraftExpanded ? 'rotate-180' : ''}`} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Countries — flag emojis */}
      {stats.countriesVisited > 0 && (
        <div className="mx-3 bg-slate-800 rounded-xl p-3 border border-slate-700/50 relative">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Countries
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-slate-600">{stats.countriesVisited}</span>
              {gated && <PremiumBadge onClick={() => setShowUpsell(true)} />}
            </div>
          </div>
          <div className={`flex flex-wrap gap-1.5 ${gated ? 'opacity-30 blur-[2px] pointer-events-none' : ''}`}>
            {stats.countryCodes.map((cc) => (
              <span
                key={cc}
                className="text-xl leading-none"
                title={cc}
                role="img"
              >
                {countryFlag(cc)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Achievement tap: celebration for unlocked, detail sheet for locked */}
      {selectedAchievement?.unlocked && (
        <AchievementCelebration
          achievement={selectedAchievement}
          onDismiss={() => setSelectedAchievement(null)}
        />
      )}
      {selectedAchievement && !selectedAchievement.unlocked && (
        <AchievementDetail
          achievement={selectedAchievement}
          onDismiss={() => setSelectedAchievement(null)}
        />
      )}

      <PremiumUpsellSheet open={showUpsell} onClose={() => setShowUpsell(false)} />
    </div>
  );
}

/** Convert ISO 3166-1 alpha-2 country code to flag emoji */
function countryFlag(cc: string): string {
  const codePoints = [...cc.toUpperCase()].map(
    (c) => 0x1f1e6 - 65 + c.charCodeAt(0),
  );
  return String.fromCodePoint(...codePoints);
}

function StatPill({ value, label, divider }: { value: string | number; label: string; divider?: boolean }) {
  return (
    <div className={`flex-1 text-center relative ${divider ? 'before:absolute before:left-0 before:top-[15%] before:h-[70%] before:w-px before:bg-slate-700/50' : ''}`}>
      <div className="text-xl font-extrabold text-slate-100 leading-tight tracking-tight">{value}</div>
      <div className="text-[9px] text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return String(n);
}
